import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, Clock, User, Bot } from 'lucide-react';
import { AuthContext } from '@/App';
import { toast } from 'sonner';

const API_URL = window.__BACKEND_URL;

const STATUS_CONFIG = {
  open: { label: 'Open', color: 'bg-blue-500/20 text-blue-300 border-blue-500/30' },
  in_progress: { label: 'In Progress', color: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30' },
  resolved: { label: 'Resolved', color: 'bg-green-500/20 text-green-300 border-green-500/30' },
  closed: { label: 'Closed', color: 'bg-gray-500/20 text-gray-300 border-gray-500/30' }
};

export default function SupportTicketDetail() {
  const { id } = useParams();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    loadTicket();
    loadMessages();
  }, [id]);

  const loadTicket = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/support/tickets/${id}`,
        { withCredentials: true }
      );
      setTicket(response.data);
    } catch (err) {
      console.error('Error loading ticket:', err);
      toast.error('Failed to load ticket');
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/support/tickets/${id}/messages`,
        { withCredentials: true }
      );
      setMessages(response.data);
    } catch (err) {
      console.error('Error loading messages:', err);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    setSending(true);
    try {
      await axios.post(
        `${API_URL}/api/support/tickets/${id}/messages`,
        { message: newMessage },
        { withCredentials: true }
      );
      setNewMessage('');
      loadMessages();
      toast.success('Message sent');
    } catch (err) {
      console.error('Error sending message:', err);
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="p-8 text-center">
        <p className="text-gray-400 mb-4">Ticket not found</p>
        <button
          onClick={() => navigate('/support/tickets')}
          className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition-colors"
        >
          Back to Tickets
        </button>
      </div>
    );
  }

  const statusConfig = STATUS_CONFIG[ticket.status] || STATUS_CONFIG.open;

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-6 border-b border-gray-800">
        <button
          onClick={() => navigate('/support/tickets')}
          className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Tickets
        </button>
        
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white mb-2">{ticket.subject}</h1>
            <div className="flex items-center gap-3 text-sm">
              <span className={`px-3 py-1 rounded-full border ${statusConfig.color}`}>
                {statusConfig.label}
              </span>
              <span className="text-gray-400">
                Created {new Date(ticket.created_at).toLocaleDateString()}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-6 space-y-4">
        {messages.map((message) => {
          const isUser = message.sender === 'user';
          const isAI = message.sender === 'ai';
          
          return (
            <div
              key={message.id}
              className={`flex gap-3 ${
                isUser ? 'flex-row-reverse' : ''
              }`}
            >
              {/* Avatar */}
              <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                isUser ? 'bg-cyan-500' : isAI ? 'bg-purple-500' : 'bg-green-500'
              }`}>
                {isUser ? (
                  <User className="w-5 h-5 text-white" />
                ) : isAI ? (
                  <Bot className="w-5 h-5 text-white" />
                ) : (
                  <span className="text-white font-bold">S</span>
                )}
              </div>

              {/* Message */}
              <div className={`flex-1 ${
                isUser ? 'text-right' : ''
              }`}>
                <div className="flex items-center gap-2 mb-1">
                  {!isUser && (
                    <span className="text-sm font-medium text-white">
                      {isAI ? 'AI Assistant' : 'Support Team'}
                    </span>
                  )}
                  <span className="text-xs text-gray-500">
                    {new Date(message.created_at).toLocaleString()}
                  </span>
                </div>
                <div className={`inline-block max-w-2xl p-4 rounded-lg ${
                  isUser
                    ? 'bg-cyan-600 text-white'
                    : isAI
                    ? 'bg-purple-500/20 border border-purple-500/30 text-gray-200'
                    : 'bg-[#1a1d23] border border-gray-800 text-gray-200'
                }`}>
                  <p className="whitespace-pre-wrap">{message.message_text}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Reply Form */}
      {ticket.status !== 'closed' && (
        <form onSubmit={handleSendMessage} className="p-6 border-t border-gray-800">
          <div className="flex gap-3">
            <textarea
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Type your message..."
              rows={3}
              className="flex-1 px-4 py-3 bg-[#1a1d23] border border-gray-800 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-600 resize-none"
              disabled={sending}
            />
            <button
              type="submit"
              disabled={sending || !newMessage.trim()}
              className="px-6 py-3 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed self-end inline-flex items-center gap-2"
            >
              <Send className="w-4 h-4" />
              {sending ? 'Sending...' : 'Send'}
            </button>
          </div>
        </form>
      )}

      {ticket.status === 'closed' && (
        <div className="p-6 border-t border-gray-800 bg-gray-800/50 text-center">
          <p className="text-gray-400">This ticket has been closed. Please create a new ticket if you need further assistance.</p>
        </div>
      )}
    </div>
  );
}
